import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface WorkflowStep {
    id: string;
    title: string;
    description: string;
    imageSrc?: string;
    mediaType?: 'image' | 'video';
    videoSrc?: string;
}

const workflowSteps: WorkflowStep[] = [
    {
        id: 'kickoff',
        title: 'Kickoff',
        description: 'We\'ll hop on a kick-off call to discuss the project and other intricacies(details) such as purpose, use cases, budget & timeline.',
        imageSrc: 'https://res.cloudinary.com/dscurx1gc/image/upload/v1769540089/kickoff_ycg4pn.png'
    },
    {
        id: 'advance',
        title: 'Advance Transfer',
        description: 'According to the revised proposals a contract will be signed by TDMA and client, with an invoice for the advance amount. Immediately after the advance payment, the project will start.',
        imageSrc: 'https://res.cloudinary.com/dscurx1gc/image/upload/v1769539813/mkxttcu0gqbouvkzlfe1.png'
    },
    {
        id: 'design',
        title: 'UX Design',
        description: 'A fine user interface and user experience prototype will be designed with proper client feedback to make sure your website looks great and feels easy to use.',
        mediaType: 'video',
        videoSrc: 'https://res.cloudinary.com/dscurx1gc/video/upload/v1769540098/uxdesign_yt68yp.mp4'
    },
    {
        id: 'development',
        title: 'Development',
        description: 'The project will be developed ensuring smooth user experience, and premium interface. Client will be informed periodically regarding the updates.',
        imageSrc: 'https://res.cloudinary.com/dscurx1gc/image/upload/v1769540087/development_x2jx7h.jpg'
    },
    {
        id: 'deployment',
        title: 'Deployment & Maintenance',
        description: 'Your project goes live with ongoing support, updates, and performance monitoring.',
        imageSrc: 'https://res.cloudinary.com/dscurx1gc/image/upload/v1769540090/deploy_maintenance_owsf79.png'
    },
    {
        id: 'handoff',
        title: 'Final Payment & Handoff',
        description: 'Complete documentation, training, and seamless transfer of all project assets.',
        imageSrc: 'https://res.cloudinary.com/dscurx1gc/image/upload/v1769540096/Handoff_dn63ly.png'
    }
];

const WorkflowSection: React.FC = () => {
    const [activeStep, setActiveStep] = useState(0);

    return (
        <section
            id="workflow"
            className="py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 bg-black text-white relative overflow-hidden"
        >
            <div className="max-w-7xl mx-auto">
                {/* Section Title */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="mb-8 md:mb-12"
                >
                    <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-[6.5rem] xl:text-[7.5rem] font-light tracking-[-0.04em] leading-none mb-3 md:mb-4">
                        Our Process
                    </h2>
                </motion.div>

                {/* Compact Workflow Tabs */}
                <div className="relative mb-8 md:mb-10">
                    {/* Horizontal scrollable tabs */}
                    <div
                        className="overflow-x-auto overflow-y-hidden pb-3 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide touch-pan-x"
                        onWheel={(e) => e.stopPropagation()}
                        onTouchStart={(e) => e.stopPropagation()}
                        onTouchMove={(e) => e.stopPropagation()}
                        style={{
                            WebkitOverflowScrolling: 'touch',
                        }}
                    >
                        <div className="flex gap-1.5 md:gap-2 w-max md:w-auto md:justify-start">
                            {workflowSteps.map((step, index) => (
                                <button
                                    key={step.id}
                                    onClick={() => setActiveStep(index)}
                                    className={`relative px-3 md:px-4 py-1.5 md:py-2 font-light text-xs md:text-sm transition-all duration-300 whitespace-nowrap rounded-md flex-shrink-0 ${activeStep === index
                                        ? 'text-white bg-white/10'
                                        : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                                        }`}
                                >
                                    {step.title}

                                    {/* Active indicator */}
                                    {activeStep === index && (
                                        <motion.div
                                            layoutId="activeTab"
                                            className="absolute bottom-0 left-0 right-0 h-[1.5px] bg-white"
                                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Bottom border line */}
                    <div className="absolute bottom-0 left-0 right-0 h-[0.5px] bg-white/10"></div>
                </div>

                {/* Content Area - Image Left, Content Right */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key={activeStep}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.3 }}
                        className="w-full cursor-grab active:cursor-grabbing"
                        drag="x"
                        dragConstraints={{ left: 0, right: 0 }}
                        dragElastic={0.2}
                        onDragEnd={(e, { offset, velocity }) => {
                            const swipe = offset.x;

                            if (swipe < -50) {
                                // Swipe Left -> Next
                                if (activeStep < workflowSteps.length - 1) {
                                    setActiveStep(activeStep + 1);
                                }
                            } else if (swipe > 50) {
                                // Swipe Right -> Prev
                                if (activeStep > 0) {
                                    setActiveStep(activeStep - 1);
                                }
                            }
                        }}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8 lg:gap-12 items-center">
                            {/* Image/Video - Left Side */}
                            <div className="relative w-full rounded-xl md:rounded-2xl overflow-hidden bg-white/5 border border-white/10 order-2 lg:order-1">
                                {workflowSteps[activeStep].mediaType === 'video' ? (
                                    <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                                        <video
                                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                                            src={workflowSteps[activeStep].videoSrc}
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                        />
                                    </div>
                                ) : (
                                    <div className="relative w-full" style={{ paddingBottom: '75%' }}>
                                        <img
                                            alt={workflowSteps[activeStep].title}
                                            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                                            src={workflowSteps[activeStep].imageSrc}
                                            draggable={false}
                                            onError={(e) => {
                                                // Fallback if image doesn't exist
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                const parent = target.parentElement;
                                                if (parent && !parent.querySelector('.placeholder')) {
                                                    const placeholder = document.createElement('div');
                                                    placeholder.className = 'placeholder absolute inset-0 flex items-center justify-center text-white/30 text-base md:text-lg';
                                                    placeholder.textContent = `${workflowSteps[activeStep].title} Image`;
                                                    parent.appendChild(placeholder);
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* Content - Right Side */}
                            <div className="order-1 lg:order-2">
                                <div className="flex items-center gap-3 mb-4 md:mb-6">
                                    <span className="inline-flex items-center justify-center w-8 h-8 md:w-10 md:h-10 rounded-full bg-white/10 text-white text-sm md:text-base font-light">
                                        {activeStep + 1}
                                    </span>
                                    <h3 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light tracking-tight">
                                        {workflowSteps[activeStep].title}
                                    </h3>
                                </div>
                                <p className="text-base sm:text-lg md:text-xl text-white/70 font-light leading-relaxed">
                                    {workflowSteps[activeStep].description}
                                </p>
                            </div>
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Progress Indicators */}
                <div className="flex justify-center gap-2 mt-10 md:mt-14">
                    {workflowSteps.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => setActiveStep(index)}
                            className={`h-1 rounded-full transition-all duration-300 ${activeStep === index
                                ? 'w-8 md:w-10 bg-white'
                                : 'w-1 bg-white/30 hover:bg-white/50'
                                }`}
                            aria-label={`Go to step ${index + 1}`}
                        />
                    ))}
                </div>
            </div>

            {/* Add custom scrollbar hiding */}
            <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
        </section>
    );
};

export default WorkflowSection;
